import { useEffect, useRef, useState } from 'preact/hooks';
import { CAMERA_HELP as W } from '../data/camerahelp';
import { camHelp } from '../live/camerahelp';
import { loadJSON, panel, reliveFocus, saveJSON, selection, setSetting, settings, showToast, viewMode } from '../state';
import { Toggle } from './chrome';
import { Icon } from './icons';

// Camera help's controls (src/live/camerahelp.ts does the looking): the switch in Sky view, next to Naked eye; the chip
// in pointing mode, which switches it on there too and says what the camera is doing; and a short note over the sky when
// cloud or haze seems to be in the way, or when it is waiting for the dark or for the sky here and now. Every word is in
// src/data/camerahelp.ts.

/** Switch camera help on or off. Off stops the camera at once (src/ui/SkyView.tsx). The first time it goes on, a word on
 *  what the rings mean. */
export function setCameraHelp(on: boolean) {
  setSetting('cameraHelp', on);
  if (on && !loadJSON<{ v?: number }>('cameraHelpTip', {}).v) {
    saveJSON('cameraHelpTip', { v: 1 });
    showToast(W.tip, 7000);
  }
}

/** In Sky view, in the group with Naked eye. */
export function CameraHelpToggle() {
  return <Toggle on={settings.value.cameraHelp} onChange={setCameraHelp} icon="camera" label={W.label} sub={W.sub} />;
}

/** What the chip says: off, its name; on, what the camera is doing. */
export function chipText(on: boolean, h = camHelp.value): string {
  if (!on) return W.chipOff;
  switch (h.state) {
    case 'seeing': return W.chip.found(h.found);
    case 'looking': return W.chip.looking;
    case 'day':
    case 'elsewhere': return W.chip.waiting;
    default: return W.chip.starting;
  }
}

/** In pointing mode, beside the bottom-left corner (Align is on the right). Rests while a card or sheet is open. */
export function CameraChip() {
  const on = settings.value.cameraHelp;
  if (viewMode.value !== 'ar' || panel.value || selection.value) return null;
  const text = chipText(on);
  return (
    <button class={`cam-chip ${on ? 'on' : ''}`} aria-pressed={on} aria-label={on ? `${W.label}: ${text}` : W.label} onClick={() => setCameraHelp(!on)}>
      <Icon name="camera" size={15} />
      <span>{text}</span>
    </button>
  );
}

/** The note's words, if there is one to show. */
export function noteText(h = camHelp.value): string | null {
  if (h.note) return W.note[h.note];
  if (h.state === 'day' || h.state === 'elsewhere') return W.note[h.state];
  return null;
}

/** How long a note that camera help is waiting (for the dark, or for the sky here and now) stays up (ms). */
const BRIEF = 6000;

/**
 * A short note over the sky, in pointing mode with camera help on. Always in the page then, so a new note is read out.
 * It gives way to a card or sheet and to the guide to something chosen (or relived); a note that camera help is waiting
 * shows for a few seconds only, the chip saying so meanwhile, while cloud or haze is noted for as long as it lasts.
 */
export function CameraNote() {
  const since = useRef({ text: '', at: 0 });
  const [, recheck] = useState(0);
  const h = camHelp.value;
  const words = noteText(h);
  if (words !== since.current.text) since.current = { text: words ?? '', at: Date.now() };
  const brief = !h.note && !!words;
  useEffect(() => {
    if (!brief) return;
    const id = setTimeout(() => recheck((x) => x + 1), BRIEF + 50);
    return () => clearTimeout(id);
  }, [brief, words]);
  if (viewMode.value !== 'ar' || !settings.value.cameraHelp) return null;
  const busy = !!panel.value || !!selection.value || !!reliveFocus.value;
  const text = busy || (brief && Date.now() - since.current.at > BRIEF) ? null : words;
  return (
    <div class="cam-note-live" role="status" aria-live="polite">
      {text && (
        <div class="cam-note">
          <Icon name={h.note ? 'cloud' : 'camera'} size={15} />
          <span>{text}</span>
        </div>
      )}
    </div>
  );
}
